import type { DocNode } from '@atlaskit/adf-schema';
import { bridgeEventEmitter, DatabaseStreamState } from '@databases/rovo-bridge';
import { useCallback, useEffect, useRef } from 'react';

import {
  BoeSubmitPromptCommand,
  OpenRovoSidePanelCommand
} from '@canvas/databases-bridge-api/commands';
import { CONFLUENCE_AFM_CLIENT_ID } from '@canvas/databases-bridge-api/types';
import { client } from '@canvas/rovo-content-bridge-api';

const getPlainTextFromADF = (prompt: DocNode): string => {
  // Extract plain text from ADF DocNode by recursively walking the node tree
  let plainText = '';

  const walk = (
    node: DocNode | { type?: string; text?: string; content?: unknown[] }
  ) => {
    if (node.type === 'text') {
      plainText += node.text ?? '';
    }

    if (node.content) {
      node.content.forEach((child) =>
        walk(child as { type?: string; text?: string; content?: unknown[] })
      );
    }
  };

  walk(prompt);

  return plainText.trim();
};

export const useDatabaseBlankObjectSubmit = ({
  contentId,
  onSubmit
}: {
  contentId: string;
  onSubmit?: () => void;
}) => {
  const unsubscribeRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = undefined;
    };
  }, []);

  return useCallback(
    (prompt: DocNode) => {
      onSubmit?.();

      let isActive = true;
      let hasSeenStreaming = false;
      let cleanup = () => {
        isActive = false;
      };
      unsubscribeRef.current?.();

      const unsubscribe = DatabaseStreamState.getInstance().subscribe((state) => {
        if (!isActive) {
          return;
        }

        if (state.isStreaming) {
          hasSeenStreaming = true;
        } else if (hasSeenStreaming) {
          cleanup();

          bridgeEventEmitter.sendEvent(OpenRovoSidePanelCommand, {
            senderId: client(`confluence-database-${contentId}`),
            receiverId: CONFLUENCE_AFM_CLIENT_ID
          });
        }
      });
      cleanup = () => {
        isActive = false;
        unsubscribe();
        if (unsubscribeRef.current === cleanup) {
          unsubscribeRef.current = undefined;
        }
      };

      unsubscribeRef.current = cleanup;

      try {
        bridgeEventEmitter.sendEvent(BoeSubmitPromptCommand, {
          senderId: client(`confluence-database-${contentId}`),
          receiverId: CONFLUENCE_AFM_CLIENT_ID,
          prompt: getPlainTextFromADF(prompt),
          contentId
        });
      } catch (error) {
        cleanup();
        throw error;
      }
    },
    [contentId, onSubmit]
  );
};
